import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { channelsApi, type Channel } from '../../api/channels'
import { useUiStore } from '../../stores/uiStore'

interface Props {
  onClose: () => void
  onCreate: (channel: Channel) => void
}

export function CreateChannelModal({ onClose, onCreate }: Props) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [username, setUsername] = useState('')
  const [isPublic, setIsPublic] = useState(true)
  const [loading, setLoading] = useState(false)
  const { addToast } = useUiStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setLoading(true)
    try {
      const channel = await channelsApi.create({
        name: title.trim(),
        description: description.trim() || undefined,
        username: username.trim() || undefined,
        is_public: isPublic,
      })
      onCreate(channel)
      onClose()
    } catch {
      addToast('error', 'Не удалось создать канал')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal onClose={onClose} title="Создать канал">
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        <div>
          <label className="text-txt-secondary text-xs font-medium mb-1.5 block">Channel Name *</label>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Мой канал"
            maxLength={64}
            required
            className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
          />
        </div>

        <div>
          <label className="text-txt-secondary text-xs font-medium mb-1.5 block">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="О чём этот канал?"
            rows={3}
            maxLength={256}
            className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta resize-none"
          />
        </div>

        <div>
          <label className="text-txt-secondary text-xs font-medium mb-1.5 block">Username (optional)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-meta text-sm">@</span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
              placeholder="username"
              maxLength={32}
              className="w-full bg-bg-input text-txt-primary rounded-xl pl-7 pr-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
            />
          </div>
        </div>

        <div>
          <label className="text-txt-secondary text-xs font-medium mb-2 block">Channel Type</label>
          <div className="space-y-2">
            {[
              { value: true, label: 'Публичный', desc: 'Любой может найти и подписаться' },
              { value: false, label: 'Приватный', desc: 'Только по приглашению' },
            ].map(({ value, label, desc }) => (
              <button
                key={String(value)}
                type="button"
                onClick={() => setIsPublic(value)}
                className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl border transition-colors text-left
                  ${isPublic === value ? 'border-accent bg-accent/10' : 'border-divider hover:bg-bg-hover'}`}
              >
                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0
                  ${isPublic === value ? 'border-accent' : 'border-txt-meta'}`}>
                  {isPublic === value && <div className="w-2 h-2 rounded-full bg-accent" />}
                </div>
                <div>
                  <p className="text-txt-primary text-sm font-medium">{label}</p>
                  <p className="text-txt-meta text-xs">{desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={!title.trim() || loading}
          className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors"
        >
          {loading ? 'Создание...' : 'Создать канал'}
        </button>
      </form>
    </Modal>
  )
}
